import { t } from "@lingui/macro";
import {
  CopySimple,
  FolderOpen,
  Lock,
  LockOpen,
  PencilSimple,
  TrashSimple,
} from "@phosphor-icons/react";
import { ResumeDto } from "@reactive-resume/dto";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { useNavigate } from "react-router-dom";

import { useDialog } from "@/client/stores/dialog";
import ResumePreview from "@/client/pages/resume-builder/components/ResumePreview";

import { BaseCard } from "./base-card";

const BUILDER_NOTE_PREFIX = "RESUME_BUILDER_V1::";

/** Try to extract builder payload from a resume's metadata.notes */
const parseBuilderPayload = (resume: any) => {
  try {
    const notes = resume?.data?.metadata?.notes ?? "";
    if (!notes.startsWith(BUILDER_NOTE_PREFIX)) return null;
    return JSON.parse(notes.slice(BUILDER_NOTE_PREFIX.length));
  } catch {
    return null;
  }
};

type Props = {
  resume: ResumeDto;
  mode?: "builder" | "legacy";
};

export const ResumeCard = ({ resume, mode = "legacy" }: Props) => {
  const navigate = useNavigate();
  const { open } = useDialog<ResumeDto>("resume");
  const { open: lockOpen } = useDialog<ResumeDto>("lock");

  const lastUpdated = dayjs().to(resume.updatedAt);

  // Parse builder payload for preview (memoised to avoid re-parsing on every render)
  const builderPayload = React.useMemo(
    () => (mode === "builder" ? parseBuilderPayload(resume) : null),
    [resume, mode],
  );

  const onOpen = () => {
    if (mode === "builder") navigate(`/resume-builder/${resume.id}`);
    else navigate(`/builder/${resume.id}`);
  };

  const onUpdate = () => {
    open("update", { id: "resume", item: resume });
  };

  const onDuplicate = () => {
    open("duplicate", { id: "resume", item: resume });
  };

  const onLockChange = () => {
    lockOpen(resume.locked ? "update" : "create", { id: "lock", item: resume });
  };

  const onDelete = () => {
    open("delete", { id: "resume", item: resume });
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <BaseCard className="space-y-0" onClick={onOpen}>
          {/* Builder preview thumbnail */}
          {builderPayload && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: 0,
              }}
            >
              <div
                style={{
                  width: 816,
                  transformOrigin: "top left",
                  transform: "scale(0.25)",
                }}
              >
                <ResumePreview
                  data={builderPayload.form}
                  templateId={builderPayload.template || "classic"}
                  customColor={builderPayload.customColor || null}
                />
              </div>
            </div>
          )}

          <AnimatePresence>
            {resume.locked && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center bg-background/75 backdrop-blur-sm"
              >
                <Lock size={42} />
              </motion.div>
            )}
          </AnimatePresence>

          <div
            className={cn(
              "absolute inset-x-0 bottom-0 z-10 flex flex-col justify-end space-y-0.5 p-4 pt-12",
              "bg-gradient-to-t from-background/80 to-transparent",
            )}
          >
            <h4 className="line-clamp-2 font-medium">{resume.title}</h4>
            <p className="line-clamp-1 text-xs opacity-75">{t`Last updated ${lastUpdated}`}</p>
          </div>
        </BaseCard>
      </ContextMenuTrigger>

      <ContextMenuContent>
        <ContextMenuItem onClick={onOpen}>
          <FolderOpen size={14} className="mr-2" />
          {t`Open`}
        </ContextMenuItem>
        {mode !== "builder" && (
          <>
            <ContextMenuItem onClick={onUpdate}>
              <PencilSimple size={14} className="mr-2" />
              {t`Rename`}
            </ContextMenuItem>
            <ContextMenuItem onClick={onDuplicate}>
              <CopySimple size={14} className="mr-2" />
              {t`Duplicate`}
            </ContextMenuItem>
            {resume.locked ? (
              <ContextMenuItem onClick={onLockChange}>
                <LockOpen size={14} className="mr-2" />
                {t`Unlock`}
              </ContextMenuItem>
            ) : (
              <ContextMenuItem onClick={onLockChange}>
                <Lock size={14} className="mr-2" />
                {t`Lock`}
              </ContextMenuItem>
            )}
          </>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem className="text-error" onClick={onDelete}>
          <TrashSimple size={14} className="mr-2" />
          {t`Delete`}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};
